import { getContent, getContentFolder, updateContentFolder, updateFolderParent } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  FolderIcon,
  FolderPlusIcon,
  PencilIcon,
  Trash2Icon,
  MoreVerticalIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "lucide-react";
import { NodeModelData, NodeModelFolderData } from "./ContentFolderViewTypes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DndProvider } from "react-dnd";
import { LoadingSmall } from "@/components/LoadingSmall";
import { Button } from "@/components/ui/button";
import { buildTree } from "./ContentFolderViewUtils";
import { useState } from "react";
import { AddFolderDialog } from "./AddFolderDialog";
import { Tree, getBackendOptions, MultiBackend, DropOptions } from "@minoru/react-dnd-treeview";
import { RenameFolderDialog } from "./RenameFolderDialog";
import { DeleteFolderDialog } from "./DeleteFolderDialog";
import { useTranslation } from "react-i18next";
import { showApiError, showSuccess } from "@/lib/toast";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { cn } from "@/lib/utils";

const withDnd = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  return function WithDndComponent(props: P) {
    return (
      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <WrappedComponent {...props} />
      </DndProvider>
    );
  };
};

interface Props {
  selectedFolderGuid?: string;
  onSelectFolder: (folderGuid?: string) => void;
}

export const ContentFolderBaseView: React.FC<Props> = withDnd(({ onSelectFolder, selectedFolderGuid }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
  const [addFolderDialogParentId, setAddFolderDialogParentId] = useState<string>();

  const { data: folders, isLoading: foldersLoading } = useQuery({
    queryKey: ["contentFolder"],
    queryFn: () => getContentFolder(String(activeFirm?.firmGuid)),
  });

  const { data: contents, isLoading: contentsLoading } = useQuery({
    queryKey: ["content"],
    queryFn: () =>
      getContent({ numberOfObjectsPerPage: 10000, pageNumber: 1 }, { FirmGuid: activeFirm?.firmGuid, IsCustom: false }),
  });

  const treeData = buildTree(folders?.data?.result || [], contents?.data?.result || []);

  const queryClient = useQueryClient();
  const updateContentFolderMutation = useMutation({
    mutationFn: ({ contentGuid, folderGuid }: { contentGuid: string; folderGuid: string }) =>
      updateContentFolder(String(activeFirm?.firmGuid), contentGuid, folderGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contentFolder"],
      });
      queryClient.invalidateQueries({
        queryKey: ["content"],
      });
      queryClient.invalidateQueries({
        queryKey: ["contentDetails"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const updateFolderParentMutation = useMutation({
    mutationFn: ({ folderGuid, parentFolderGuid }: { folderGuid: string; parentFolderGuid: string }) =>
      updateFolderParent(String(activeFirm?.firmGuid), folderGuid, parentFolderGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contentFolder"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const handleDrop = (_tree: any, options: DropOptions<NodeModelData>) => {
    const { dragSource, dropTarget } = options;
    if (dragSource?.data?.type === "content" && dropTarget?.data?.type === "folder") {
      updateContentFolderMutation.mutate({
        contentGuid: dragSource.data.entity.contentGuid,
        folderGuid: dropTarget.data.entity.id,
      });
    } else if (dragSource?.data?.type === "folder" && dropTarget?.data?.type === "folder") {
      updateFolderParentMutation.mutate({
        folderGuid: dragSource.data.entity.id,
        parentFolderGuid: dropTarget.data.entity.id,
      });
    }
  };

  if (foldersLoading || contentsLoading) return <LoadingSmall />;

  return (
    <>
      <div className="mb-2">
        <Button
          size="sm"
          onClick={() => {
            setAddFolderDialogOpen(true);
            setAddFolderDialogParentId(undefined);
          }}
        >
          <FolderPlusIcon className="h-4 w-4" />
          {t("folder.addFolder")}
        </Button>
      </div>
      <LoadingOverlay
        isLoading={
          foldersLoading ||
          contentsLoading ||
          updateContentFolderMutation.isPending ||
          updateFolderParentMutation.isPending
        }
      >
        <Tree
          tree={treeData}
          rootId={"0"}
          onDrop={handleDrop}
          classes={{
            root: "overflow-y-auto min-h-[200px] border rounded-md",
          }}
          initialOpen={false}
          enableAnimateExpand
          render={(node, { depth, isOpen, onToggle, isDropTarget }) => {
            // Only show folders
            if (node.data?.type === "folder") {
              return (
                <Folder
                  type="folder"
                  depth={depth}
                  entity={node.data.entity}
                  isOpen={isOpen}
                  onToggle={onToggle}
                  isDropTarget={isDropTarget}
                  onClickFolder={() => {
                    if (selectedFolderGuid === (node.data as NodeModelFolderData).entity.id) {
                      onSelectFolder(undefined);
                    } else {
                      onSelectFolder((node.data as NodeModelFolderData).entity.id);
                    }
                  }}
                  isSelected={selectedFolderGuid === (node.data as NodeModelFolderData).entity.id}
                />
              );
            }
            return <></>;
          }}
        />
      </LoadingOverlay>
      {addFolderDialogOpen && (
        <AddFolderDialog
          parentId={addFolderDialogParentId}
          onClose={() => {
            setAddFolderDialogOpen(false);
          }}
          onSuccessfullyAdded={() => {
            setAddFolderDialogOpen(false);
          }}
        />
      )}
    </>
  );
});

const Folder: React.FC<
  NodeModelFolderData & {
    depth: number;
    onToggle: () => void;
    isOpen: boolean;
    isDropTarget: boolean;
    onClickFolder: () => void;
    isSelected: boolean;
  }
> = (props) => {
  const { t } = useTranslation();
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState<boolean>();
  const [renameFolderDialogOpen, setRenameFolderDialogOpen] = useState<boolean>();
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState<boolean>();

  if (props.type !== "folder") return null;

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-2 px-2 hover:bg-blue-50 rounded-md text-sm cursor-pointer group transition-colors",
          {
            "bg-blue-50": props.isDropTarget,
            "bg-blue-100": props.isSelected,
          }
        )}
        style={{ marginLeft: props.depth * 10 }}
        onClick={() => {
          props.onClickFolder();
          if (!props.isOpen) {
            props.onToggle();
          }
        }}
      >
        {props.isOpen ? (
          <ChevronDownIcon
            className="h-4 w-4 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              props.onToggle();
            }}
          />
        ) : (
          <ChevronRightIcon
            className="h-4 w-4 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              props.onToggle();
            }}
          />
        )}
        <div className="flex-1 flex items-center gap-2" style={{ color: props.entity.color || "#444444" }}>
          <FolderIcon className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">{props.entity.name}</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8 p-0 invisible group-hover:visible hover:bg-blue-100">
              <MoreVerticalIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setRenameFolderDialogOpen(true);
              }}
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              {t("common.update")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setAddFolderDialogOpen(true);
              }}
            >
              <FolderPlusIcon className="h-4 w-4 mr-2" />
              {t("folder.addSubfolder")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setDeleteFolderDialogOpen(true);
              }}
              className="text-red-600 hover:bg-red-50"
            >
              <Trash2Icon className="h-4 w-4 mr-2" />
              {t("common.deleteEntity.buttonText")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {addFolderDialogOpen && (
        <AddFolderDialog
          parentId={props.entity.id}
          onClose={() => {
            setAddFolderDialogOpen(undefined);
          }}
          onSuccessfullyAdded={() => {
            setAddFolderDialogOpen(undefined);
          }}
        />
      )}
      {renameFolderDialogOpen && (
        <RenameFolderDialog
          onClose={() => {
            setRenameFolderDialogOpen(undefined);
          }}
          onSuccessfullyRenamed={() => {
            setRenameFolderDialogOpen(undefined);
          }}
          folder={props.entity}
        />
      )}
      {deleteFolderDialogOpen && (
        <DeleteFolderDialog
          folder={props.entity}
          onClose={() => {
            setDeleteFolderDialogOpen(undefined);
          }}
          onSuccessfullyDeleted={() => {
            setDeleteFolderDialogOpen(undefined);
          }}
        />
      )}
    </>
  );
};
